import { IMarketoField } from 'marketing-site/lib/marketo/@types/fields'
import { captureException } from 'marketing-site/lib/sentry'
import {
  FieldType,
  ICheckboxField,
  IEmailField,
  IFormField,
  IHiddenField,
  IHtmlTextField,
  IRadioButtonsField,
  ISelectField,
  ITextareaField,
  ITextField,
} from 'marketing-site/src/library/elements/Form/@types/FormField'
import { ITelephoneField } from './../../../../elements/Form/@types/FormField'
import transformAutoFillProp from './transformAutoFillProp'
import { MarketoFieldTransformError } from './transformError'

export interface ITransformFieldProps {
  field: IMarketoField
  forceBusinessEmail: boolean
  hideLabel: boolean
  overrideHidden: boolean
}

export default function transformField(params: ITransformFieldProps): IFormField | undefined {
  const { field, forceBusinessEmail, hideLabel, overrideHidden } = params
  const transformedBaseField = {
    id: field.id,
    required: field.required,
  }
  setAutoFill(transformedBaseField, field)

  if (overrideHidden) {
    return {
      ...transformedBaseField,
      type: 'hidden',
    }
  }

  switch (field.dataType) {
    case 'text':
    case 'textArea':
      const transformedTextField: ITextField | ITextareaField = {
        ...transformedBaseField,
        type: field.dataType,
        label: hideLabel ? undefined : field.label,
        placeholder: field.hintText,
        maxLength: field.maxLength,
      }

      return transformedTextField
    case 'telephone':
      const transformedPhoneField: ITelephoneField = {
        ...transformedBaseField,
        type: FieldType.PHONE,
        label: hideLabel ? undefined : field.label,
        placeholder: field.hintText,
      }

      return transformedPhoneField
    case 'email':
      const transformedEmailField: IEmailField = {
        ...transformedBaseField,
        type: field.dataType,
        label: hideLabel ? undefined : field.label,
        placeholder: field.hintText,
        maxLength: field.maxLength,
        forceBusinessEmail: forceBusinessEmail,
      }

      return transformedEmailField
    case 'hidden':
      const transformedHiddenField: IHiddenField = {
        ...transformedBaseField,
        type: field.dataType,
      }

      return transformedHiddenField
    case 'checkbox':
      if (typeof field.label === 'undefined')
        throw new MarketoFieldTransformError(`Checkbox field ${field.id} is missing a label.`)

      const transformedCheckboxField: ICheckboxField = {
        ...transformedBaseField,
        type: field.dataType,
        label: field.label,
      }

      return transformedCheckboxField
    case 'radioButtons':
      const transformedRadioButtonsField: IRadioButtonsField = {
        ...transformedBaseField,
        type: field.dataType,
        values: field.fieldMetaData.values,
        label: field.label,
      }
      return transformedRadioButtonsField
    case 'select':
      const transformedSelectField: ISelectField = {
        ...transformedBaseField,
        type: field.dataType,
        values: field.fieldMetaData.values.filter((value) => !value.isDefault),
        placeholder: field.fieldMetaData.values.find((value) => value.isDefault)?.label || '',
        label: hideLabel ? undefined : field.label,
      }

      return transformedSelectField
    case 'htmltext':
      if (typeof field.text === 'undefined')
        throw new MarketoFieldTransformError(`HTML text field ${field.id} is missing text prop.`)

      const transformedHtmltextField: IHtmlTextField = {
        ...transformedBaseField,
        type: field.dataType,
        text: field.text,
      }

      return transformedHtmltextField
    case 'profiling':
      return
    default:
      throw new MarketoFieldTransformError(
        `Trying to transform unknown MarketoField type: ${field.dataType}`,
      )
  }
}

function setAutoFill(toField: Partial<IFormField>, fromField: IMarketoField) {
  try {
    toField.autoFill = fromField.autoFill && transformAutoFillProp(fromField.autoFill)
  } catch (error) {
    if (error instanceof MarketoFieldTransformError) {
      const errMsg = `Could not transform autoFill prop on field ${fromField.id} because ${error.message}`
      captureException(errMsg)
    }
  }
}
